<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { fade, fly } from 'svelte/transition';
  import { page } from '$app/stores';
  import { goto } from '$app/navigation';
  import Header from '$lib/components/sections/Header.svelte';
  import Footer from '$lib/components/sections/Footer.svelte';
  import Button from '$lib/components/primitives/Button.svelte';
  import Icon from '$lib/components/primitives/Icon.svelte';
  import WeeklySearch from '$lib/components/weekly/WeeklySearch.svelte';
  import RSSDropdown from '$lib/components/weekly/RSSDropdown.svelte';
  import { formatReadingTime } from '$lib/utils/formatReadingTime';
  import type { PageData } from './$types';

  let { data }: { data: PageData } = $props();
  
  let visible = $state(true); // SSR: start visible so hero + <h1> are in server HTML (Ahrefs: "H1 tag missing"). onMount re-set is a no-op; entrance animations still play on client-side navigation.
  let selectedCategory = $state('all');
  let searchQuery = $state('');
  
  // Format date according to locale
  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    const localeCode = data.locale === 'en' ? 'en-US' : data.locale;
    
    // Short format for list items
    return date.toLocaleDateString(localeCode, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
  
  // Long format for featured content
  function formatDateLong(dateString: string): string {
    const date = new Date(dateString);
    const localeCode = data.locale === 'en' ? 'en-US' : data.locale;
    
    return date.toLocaleDateString(localeCode, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  // Categories for filtering
  const categories = $derived([
    { id: 'all', label: $_('weekly.categories.all') },
    { id: 'blog', label: $_('weekly.categories.blog') },
    { id: 'product-updates', label: $_('weekly.categories.productUpdates') },
    { id: 'press', label: $_('weekly.categories.press') },
    { id: 'customer-stories', label: $_('weekly.categories.customerStories') }
  ]);
  
  // Get initial category from URL params
  $effect(() => {
    const urlCategory = $page.url.searchParams.get('category');
    if (urlCategory && categories.some(c => c.id === urlCategory)) {
      selectedCategory = urlCategory;
    }
  });
  
  // Update URL when category changes
  function selectCategory(categoryId: string) {
    selectedCategory = categoryId;
    const url = new URL($page.url);
    if (categoryId === 'all') {
      url.searchParams.delete('category');
    } else {
      url.searchParams.set('category', categoryId);
    }
    goto(url.toString(), { replaceState: true, noScroll: true });
  }
  
  onMount(() => {
    visible = true;
  });
  
  // Get author info from authors data
  function getAuthorInfo(authorName: string) {
    const authorData = data.authorsData?.[authorName];
    return {
      name: authorName,
      profileImage: authorData?.profileImage,
      bio: authorData?.bio
    };
  }

  // Featured content - Use real blog post if available
  const featuredPost = $derived(
    data.featuredPost ? {
      id: data.featuredPost.slug,
      categoryId: 'blog',
      tag: 'Blog',
      title: data.featuredPost.title,
      description: data.featuredPost.excerpt,
      image: data.featuredPost.featureImage,
      date: data.featuredPost.date,
      readTime: data.featuredPost.readingTime || 5,
      author: getAuthorInfo(data.featuredPost.author),
      slug: data.featuredPost.slug
    } : {
      id: 'placeholder',
      categoryId: 'product-updates',
      tag: 'Coming Soon',
      title: $_('weekly.main.newContentComingSoon'),
      description: $_('weekly.main.newContentComingSoon'),
      image: null,
      date: new Date().toISOString().split('T')[0],
      readTime: 5,
      author: {
        name: 'Heyweek Team',
        profileImage: null,
        bio: null
      },
      slug: null
    }
  );

  // Recent updates - Use all 4 real blog posts
  const recentUpdates = $derived([
    ...(data.recentBlogPosts?.map(post => ({
      id: post.slug,
      categoryId: 'blog',
      category: 'Blog',
      title: post.title,
      description: post.excerpt,
      date: post.date,
      image: post.featureImage,
      tags: post.tags,
      slug: post.slug
    })) || []),
    // Only add placeholders if we have less than 4 real posts
    ...(data.recentBlogPosts && data.recentBlogPosts.length < 4 ? [
      {
        id: 'product-placeholder',
        categoryId: 'product-updates',
        category: 'Product Update',
        title: $_('weekly.main.productUpdatesComingSoon'),
        description: $_('weekly.main.productUpdatesComingSoon'),
        date: new Date().toISOString().split('T')[0],
        image: null,
        tags: ['Coming Soon'],
        slug: null
      },
      {
        id: 'customer-placeholder',
        categoryId: 'customer-stories',
        category: 'Customer Story',
        title: $_('weekly.main.customerSuccessStoriesOnTheWay'),
        description: $_('weekly.main.customerSuccessStoriesOnTheWay'),
        date: new Date().toISOString().split('T')[0],
        image: null,
        tags: ['Coming Soon'],
        slug: null
      }
    ].slice(0, 4 - (data.recentBlogPosts?.length || 0)) : [])
  ]);

  // Content sections with real blog data and placeholders
  const contentSections = $derived([
    {
      categoryId: 'blog',
      title: $_('weekly.contentSections.fromBlog'),
      link: '/weekly/blog',
      items: data.sidebarBlogPosts?.map(post => ({
        id: post.slug,
        title: post.title,
        date: post.date,
        showDate: true,
        category: post.category,
        slug: post.slug
      })) || [
        {
          id: 'blog-placeholder',
          title: $_('weekly.main.newContentComingSoon'),
          showDate: false,
          category: 'Blog'
        }
      ]
    }
  ]);
  
  // Customer stories placeholder
  const hasCustomerStories = false;
  
  // Helper function to check if content matches search query
  function matchesSearch(item: any, query: string): boolean {
    if (!query) return true;
    
    const searchLower = query.toLowerCase();
    
    // Check title
    if (item.title?.toLowerCase().includes(searchLower)) return true;
    
    // Check description/excerpt
    if (item.description?.toLowerCase().includes(searchLower)) return true;
    if (item.excerpt?.toLowerCase().includes(searchLower)) return true;
    
    // Check author
    if (item.author?.name?.toLowerCase().includes(searchLower)) return true;
    if (item.author?.toLowerCase().includes(searchLower)) return true;
    
    // Check tags
    if (item.tags?.some((tag: string) => tag.toLowerCase().includes(searchLower))) return true;
    
    // Check category
    if (item.category?.toLowerCase().includes(searchLower)) return true;
    if (item.categoryId?.toLowerCase().includes(searchLower)) return true;
    
    return false;
  }
  
  // Filter content based on selected category and search
  let filteredFeaturedPost = $derived(
    (selectedCategory === 'all' || featuredPost.categoryId === selectedCategory) && 
    matchesSearch(featuredPost, searchQuery)
      ? featuredPost 
      : null
  );
  
  let filteredRecentUpdates = $derived(
    recentUpdates.filter(update => {
      // Check category filter
      const matchesCategory = selectedCategory === 'all' || update.categoryId === selectedCategory;
      
      // Check search filter
      const matchesSearchQuery = matchesSearch(update, searchQuery);
      
      return matchesCategory && matchesSearchQuery;
    })
  );
  
  let filteredContentSections = $derived(
    contentSections
      .map(section => {
        // Filter items within each section based on search
        if (searchQuery) {
          const filteredItems = section.items.filter(item => matchesSearch(item, searchQuery));
          
          // Only include section if it has matching items
          if (filteredItems.length === 0) return null;
          
          return {
            ...section,
            items: filteredItems
          };
        }
        
        // For category filtering, keep original behavior
        return section;
      })
      .filter(section => {
        // Remove null sections (those with no matching items)
        if (section === null) return false;
        
        // Apply category filter
        return selectedCategory === 'all' || section.categoryId === selectedCategory;
      })
  );
  
  // Filter content based on search
  function handleSearch(query: string) {
    searchQuery = query;
  }
</script>

<svelte:head>
  <title>{$_('weekly.hero.title')} - Heyweek</title>
  <meta name="description" content={$_('weekly.hero.description')} />
	<meta property="og:title" content="{$_('weekly.hero.title')} - Heyweek" />
	<meta property="og:description" content={$_('weekly.hero.description')} />
</svelte:head>

<Header />

<main class="weekly">
  {#if visible}
    <!-- Hero Section -->
    <section class="hero" in:fade={{ duration: 400 }}>
      <div class="container">
        <div class="hero__header" in:fly={{ y: 10, duration: 400, delay: 50 }}>
          <div class="hero__text">
            <h1 class="hero__title">{$_('weekly.hero.title')}</h1>
            <p class="hero__description">{$_('weekly.hero.description')}</p>
          </div>
        </div>
        
        <div class="hero__toolbar" in:fly={{ y: 10, duration: 400, delay: 100 }}>
          <div class="hero__categories">
            {#each categories as category}
              <button
                class="category-btn"
                class:active={selectedCategory === category.id}
                onclick={() => selectCategory(category.id)}
              >
                {category.label}
              </button>
            {/each}
          </div>
          
          <div class="hero__actions">
            <WeeklySearch onSearch={handleSearch} placeholder={$_('weekly.search.placeholder')} />
            <RSSDropdown />
          </div>
        </div>
      </div>
    </section>
    
    <!-- Search Active Indicator -->
    {#if searchQuery}
      <div class="search-indicator" in:fade={{ duration: 200 }}>
        <div class="container">
          <div class="search-indicator__content">
            <span class="search-indicator__text">
              {$_('weekly.search.searching', { values: { query: searchQuery } })}
            </span>
            <button 
              class="search-indicator__clear"
              onclick={() => searchQuery = ''}
            >
              {$_('blog.weeklySearch.clearSearch')}
            </button>
          </div>
        </div>
      </div>
    {/if}
    
    <!-- Featured Content -->
    {#if filteredFeaturedPost}
      <section class="featured" in:fly={{ y: 20, duration: 500, delay: 300 }}>
        <div class="container">
          {#if featuredPost.slug}
            <a href="/weekly/blog/{featuredPost.slug}" class="featured-link">
              <article class="featured-post">
                <div class="featured-post__image">
                  {#if featuredPost.image}
                    <img 
                      src={featuredPost.image} 
                      alt={featuredPost.title}
                      loading="lazy"
                      onload={(e) => e.currentTarget.style.opacity = '1'}
                      onerror={(e) => {
                        e.currentTarget.style.display = 'none';
                        const placeholder = e.currentTarget.nextElementSibling;
                        if (placeholder) placeholder.style.display = 'flex';
                      }}
                      style="opacity: 0; transition: opacity 0.3s ease;"
                    />
                    <div class="image-placeholder" style="display: none;">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                    </div>
                  {:else}
                    <div class="image-placeholder">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                    </div>
                  {/if}
                </div>
                <div class="featured-post__content">
                  <div class="featured-post__tags">
                    <span class="post-tag">{featuredPost.tag}</span>
                  </div>
                  <h2 class="featured-post__title">{featuredPost.title}</h2>
                  <p class="featured-post__description">{featuredPost.description}</p>
                  <div class="featured-post__meta">
                    <div class="author">
                      {#if featuredPost.author.profileImage}
                        <img 
                          class="author__avatar"
                          src={featuredPost.author.profileImage} 
                          alt={featuredPost.author.name}
                          loading="lazy"
                        />
                      {:else}
                        <div class="author__avatar author__avatar--placeholder"></div>
                      {/if}
                      <div class="author__info">
                        <div class="author__name">{featuredPost.author.name}</div>
                        <div class="author__role">{featuredPost.author.role || 'Author'}</div>
                      </div>
                    </div>
                    <div class="post-meta">
                      <span>{formatDateLong(featuredPost.date)}</span>
                      <span class="separator">·</span>
                      <span>{formatReadingTime(featuredPost.readTime, $_)}</span>
                    </div>
                  </div>
                </div>
              </article>
            </a>
          {:else}
            <div class="featured-link">
              <article class="featured-post">
                <div class="featured-post__image">
                  {#if featuredPost.image}
                    <img 
                      src={featuredPost.image} 
                      alt={featuredPost.title}
                      loading="lazy"
                      onload={(e) => e.currentTarget.style.opacity = '1'}
                      onerror={(e) => {
                        e.currentTarget.style.display = 'none';
                        const placeholder = e.currentTarget.nextElementSibling;
                        if (placeholder) placeholder.style.display = 'flex';
                      }}
                      style="opacity: 0; transition: opacity 0.3s ease;"
                    />
                    <div class="image-placeholder" style="display: none;">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                    </div>
                  {:else}
                    <div class="image-placeholder">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                    </div>
                  {/if}
                </div>
                <div class="featured-post__content">
                  <div class="featured-post__tags">
                    <span class="post-tag">{featuredPost.tag}</span>
                  </div>
                  <h2 class="featured-post__title">{featuredPost.title}</h2>
                  <p class="featured-post__description">{featuredPost.description}</p>
                  <div class="featured-post__meta">
                    <div class="author">
                      {#if featuredPost.author.profileImage}
                        <img 
                          class="author__avatar"
                          src={featuredPost.author.profileImage} 
                          alt={featuredPost.author.name}
                          loading="lazy"
                        />
                      {:else}
                        <div class="author__avatar author__avatar--placeholder"></div>
                      {/if}
                      <div class="author__info">
                        <div class="author__name">{featuredPost.author.name}</div>
                        <div class="author__role">{featuredPost.author.role || 'Author'}</div>
                      </div>
                    </div>
                    <div class="post-meta">
                      <span>{formatDateLong(featuredPost.date)}</span>
                      <span class="separator">·</span>
                      <span>{formatReadingTime(featuredPost.readTime, $_)}</span>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          {/if}
        </div>
      </section>
    {/if}
    
    <!-- Recent Updates -->
    {#if filteredRecentUpdates.length > 0}
      <section class="recent" in:fly={{ y: 20, duration: 500, delay: 400 }}>
        <div class="container">
          <div class="section-header">
            <h2 class="section-title">{$_('weekly.sections.recentUpdates')}</h2>
            <a href="/weekly/blog" class="section-link">
              {$_('weekly.viewAll')} →
            </a>
          </div>
          
          <div class="recent__grid">
            {#each filteredRecentUpdates as update, index}
              {#if update.slug}
                <a href="/weekly/blog/{update.slug}" class="post-link" in:fly={{ y: 20, duration: 400, delay: 50 + index * 30 }}>
                  <article class="post-card">
                  <div class="post-card__image">
                    {#if update.image}
                      <img 
                        src={update.image} 
                        alt={update.title}
                        loading="lazy"
                        onload={(e) => e.currentTarget.style.opacity = '1'}
                        onerror={(e) => {
                          e.currentTarget.style.display = 'none';
                          const placeholder = e.currentTarget.nextElementSibling;
                          if (placeholder) placeholder.style.display = 'flex';
                        }}
                        style="opacity: 0; transition: opacity 0.3s ease;"
                      />
                      <div class="image-placeholder" style="display: none;">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                          <polyline points="21 15 16 10 5 21"></polyline>
                        </svg>
                      </div>
                    {:else}
                      <div class="image-placeholder">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                          <polyline points="21 15 16 10 5 21"></polyline>
                        </svg>
                      </div>
                    {/if}
                  </div>
                  <div class="post-card__content">
                    <div class="post-card__header">
                      <span class="post-card__category">{update.category}</span>
                      <span class="post-card__date">{formatDate(update.date)}</span>
                    </div>
                    <h3 class="post-card__title">{update.title}</h3>
                    <p class="post-card__description">{update.description}</p>
                    <div class="post-card__tags">
                      {#each update.tags.slice(0, 2) as tag}
                        <span class="post-card__tag">{tag}</span>
                      {/each}
                    </div>
                  </div>
                  </article>
                </a>
              {:else}
                <div class="post-link post-link--placeholder" in:fly={{ y: 20, duration: 400, delay: 50 + index * 30 }}>
                  <article class="post-card">
                    <div class="post-card__image">
                      {#if update.image}
                        <img 
                          src={update.image} 
                          alt={update.title}
                          loading="lazy"
                          onload={(e) => e.currentTarget.style.opacity = '1'}
                          onerror={(e) => {
                            e.currentTarget.style.display = 'none';
                            const placeholder = e.currentTarget.nextElementSibling;
                            if (placeholder) placeholder.style.display = 'flex';
                          }}
                          style="opacity: 0; transition: opacity 0.3s ease;"
                        />
                        <div class="image-placeholder" style="display: none;">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                            <polyline points="21 15 16 10 5 21"></polyline>
                          </svg>
                        </div>
                      {:else}
                        <div class="image-placeholder">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                            <polyline points="21 15 16 10 5 21"></polyline>
                          </svg>
                        </div>
                      {/if}
                    </div>
                    <div class="post-card__content">
                      <div class="post-card__header">
                        <span class="post-card__category">{update.category}</span>
                        <span class="post-card__date">{formatDate(update.date)}</span>
                      </div>
                      <h3 class="post-card__title">{update.title}</h3>
                      <p class="post-card__description">{update.description}</p>
                      <div class="post-card__tags">
                        {#each update.tags.slice(0, 2) as tag}
                          <span class="post-card__tag">{tag}</span>
                        {/each}
                      </div>
                    </div>
                  </article>
                </div>
              {/if}
            {/each}
          </div>
        </div>
      </section>
    {/if}
    
    <!-- Content Sections -->
    {#if filteredContentSections.length > 0}
      <section class="content-sections" in:fly={{ y: 20, duration: 500, delay: 500 }}>
        <div class="container">
          <div class="content-sections__grid">
            {#each filteredContentSections as section}
              <div class="content-section">
                <div class="content-section__header">
                  <h3 class="content-section__title">{section.title}</h3>
                  <a href={section.link} class="content-section__link">
                    {$_('weekly.viewAll')} →
                  </a>
                </div>
                <div class="content-section__items">
                  {#each section.items as item}
                    {#if item.slug}
                      <a href="/weekly/blog/{item.slug}" class="content-item">
                        <div class="content-item__content">
                          <h4 class="content-item__title">{item.title}</h4>
                          <div class="content-item__meta">
                            {#if item.showDate !== false}
                              <span class="content-item__date">{formatDate(item.date)}</span>
                            {/if}
                            <span class="content-item__category">{item.category}</span>
                          </div>
                        </div>
                      </a>
                    {:else}
                      <div class="content-item content-item--placeholder">
                        <div class="content-item__content">
                          <h4 class="content-item__title">{item.title}</h4>
                          {#if item.showDate !== false}
                            <div class="content-item__meta">
                              <span class="content-item__date">{formatDate(item.date)}</span>
                              <span class="content-item__category">{item.category}</span>
                            </div>
                          {/if}
                        </div>
                      </div>
                    {/if}
                  {/each}
                </div>
              </div>
            {/each}
          </div>
        </div>
      </section>
    {/if}
    
    <!-- No Results Message -->
    {#if searchQuery && !filteredFeaturedPost && filteredRecentUpdates.length === 0 && filteredContentSections.length === 0}
      <section class="no-results-section" in:fade={{ duration: 300 }}>
        <div class="container">
          <div class="no-results">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <h2 class="no-results__title">{$_('weekly.main.noResultsFound')}</h2>
            <p class="no-results__description">
              {$_('weekly.search.hint')}
            </p>
            <button 
              class="no-results__clear"
              onclick={() => searchQuery = ''}
            >
              {$_('blog.weeklySearch.clearSearch')}
            </button>
          </div>
        </div>
      </section>
    {/if}
    
    <!-- Customer Stories -->
    {#if selectedCategory === 'all' || selectedCategory === 'customer-stories'}
      <section class="stories" in:fly={{ y: 20, duration: 500, delay: 600 }}>
        <div class="container">
          <div class="section-header">
            <h2 class="section-title">{$_('weekly.sections.customerStories')}</h2>
          </div>
          
          {#if !hasCustomerStories}
            <div class="stories-placeholder">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              <h3 class="stories-placeholder__title">{$_('weekly.main.customerStoriesComingSoon')}</h3>
              <p class="stories-placeholder__description">
                {$_('weekly.main.customerSuccessStoriesOnTheWay')}
              </p>
            </div>
          {/if}
        </div>
      </section>
    {/if}
    
  {/if}
</main>

<Footer />

<style>
  .weekly {
    min-height: 100vh;
  }
  
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }
  
  /* Hero */
  .hero {
    padding: 2rem 0 1.5rem;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-light);
  }
  
  .hero__header {
    margin-bottom: 1.5rem;
  }
  
  .hero__text {
    max-width: 600px;
  }
  
  .hero__title {
    font-size: var(--font-size-4xl);
    font-weight: var(--font-weight-bold);
    color: var(--text-primary);
    margin-bottom: 0.5rem;
    letter-spacing: var(--letter-spacing-tight);
    line-height: var(--line-height-tight);
  }
  
  .hero__description {
    font-size: var(--font-size-base);
    color: var(--text-secondary);
    line-height: var(--line-height-normal);
  }
  
  .hero__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
    flex-wrap: wrap;
  }
  
  .hero__categories {
    display: flex;
    gap: 0.375rem;
    flex-wrap: wrap;
  }
  
  .hero__actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  .category-btn {
    padding: 0.375rem 0.875rem;
    background: transparent;
    border: 1px solid var(--border-light);
    border-radius: 6px;
    color: var(--text-secondary);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
  }
  
  .category-btn:hover {
    background: var(--bg-primary);
    border-color: var(--border-default);
    color: var(--text-primary);
  }
  
  .category-btn.active {
    background: var(--text-primary);
    border-color: var(--text-primary);
    color: var(--bg-primary);
  }
  
  /* Featured */
  .featured {
    padding: 4rem 0;
  }
  
  .featured-link {
    text-decoration: none;
    color: inherit;
    display: block;
  }
  
  .featured-link:hover .featured-post__title {
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  
  .featured-post {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    align-items: center;
  }
  
  .featured-post__image {
    width: 100%;
    height: 400px;
    background: var(--bg-secondary);
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid var(--border-light);
  }
  
  .featured-post__image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .image-placeholder {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .image-placeholder svg {
    color: var(--text-tertiary);
    opacity: 0.3;
  }
  
  .featured-post__tags {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;
  }
  
  .post-tag {
    padding: 0.25rem 0.5rem;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-light);
    border-radius: 4px;
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-medium);
    text-transform: uppercase;
    letter-spacing: var(--letter-spacing-wider);
    color: var(--text-secondary);
  }
  
  .featured-post__title {
    font-size: var(--font-size-4xl);
    font-weight: var(--font-weight-bold);
    color: var(--text-primary);
    margin-bottom: 1rem;
    line-height: var(--line-height-tight);
  }
  
  .featured-post__description {
    font-size: var(--font-size-lg);
    color: var(--text-secondary);
    line-height: var(--line-height-relaxed);
    margin-bottom: 1.5rem;
  }
  
  .featured-post__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
  }
  
  .author {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  .author__avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    filter: grayscale(100%);
  }
  
  .author__avatar--placeholder {
    background: var(--bg-tertiary);
    border: 1px solid var(--border-light);
  }
  
  .author__name {
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    font-size: var(--font-size-sm);
  }
  
  .author__role {
    font-size: var(--font-size-sm);
    color: var(--text-tertiary);
  }
  
  .post-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: var(--font-size-sm);
    color: var(--text-tertiary);
  }
  
  .separator {
    color: var(--text-tertiary);
  }
  
  /* Recent */
  .recent {
    padding: 4rem 0;
    background: var(--bg-secondary);
  }
  
  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2rem;
  }
  
  .section-title {
    font-size: var(--font-size-3xl);
    font-weight: var(--font-weight-bold);
    color: var(--text-primary);
    line-height: var(--line-height-tight);
  }
  
  .section-link {
    color: var(--text-secondary);
    text-decoration: none;
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-medium);
    transition: color 0.2s ease;
  }
  
  .section-link:hover {
    color: var(--text-primary);
  }
  
  .recent__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.5rem;
  }
  
  .post-link {
    text-decoration: none;
    color: inherit;
    display: block;
    height: 100%;
  }
  
  .post-link--placeholder {
    cursor: default;
    opacity: 0.7;
  }
  
  .post-link:hover .post-card__title {
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  
  .post-link--placeholder:hover .post-card__title {
    text-decoration: none;
  }
  
  .post-card {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--bg-primary);
    border: 1px solid var(--border-light);
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.2s ease;
  }
  
  .post-link:not(.post-link--placeholder):hover .post-card {
    border-color: var(--border-default);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
  
  .post-card__image {
    width: 100%;
    height: 160px;
    background: var(--bg-tertiary);
    overflow: hidden;
  }
  
  .post-card__image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .post-card__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 1.25rem;
  }
  
  .post-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
    font-size: var(--font-size-xs);
  }
  
  .post-card__category {
    color: var(--text-tertiary);
    font-weight: var(--font-weight-medium);
    text-transform: uppercase;
    letter-spacing: var(--letter-spacing-wider);
  }
  
  .post-card__date {
    color: var(--text-tertiary);
  }
  
  .post-card__title {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: 0.75rem;
    line-height: var(--line-height-snug);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .post-card__description {
    flex: 1;
    font-size: var(--font-size-base);
    color: var(--text-secondary);
    line-height: var(--line-height-normal);
    margin-bottom: 1rem;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .post-card__tags {
    display: flex;
    gap: 0.375rem;
    flex-wrap: wrap;
  }
  
  .post-card__tag {
    padding: 0.125rem 0.375rem;
    background: var(--bg-tertiary);
    border-radius: 3px;
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-medium);
    text-transform: uppercase;
    letter-spacing: var(--letter-spacing-wider);
    color: var(--text-tertiary);
  }
  
  /* Search Indicator */
  .search-indicator {
    padding: 1rem 0;
    background: var(--bg-tertiary);
    border-bottom: 1px solid var(--border-light);
  }
  
  .search-indicator__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }
  
  .search-indicator__text {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
  }
  
  .search-indicator__text strong {
    color: var(--text-primary);
    font-weight: var(--font-weight-semibold);
  }
  
  .search-indicator__clear {
    padding: 0.25rem 0.75rem;
    background: var(--bg-primary);
    border: 1px solid var(--border-light);
    border-radius: 6px;
    color: var(--text-secondary);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    cursor: pointer;
    transition: all 0.15s ease;
  }
  
  .search-indicator__clear:hover {
    background: var(--bg-secondary);
    border-color: var(--border-default);
    color: var(--text-primary);
  }
  
  /* No Results */
  .no-results-section {
    padding: 4rem 0;
  }
  
  .no-results {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
    text-align: center;
    background: var(--bg-secondary);
    border: 1px solid var(--border-light);
    border-radius: 12px;
    max-width: 600px;
    margin: 0 auto;
  }
  
  .no-results svg {
    color: var(--text-tertiary);
    opacity: 0.5;
    margin-bottom: 1.5rem;
  }
  
  .no-results__title {
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: 0.75rem;
    line-height: var(--line-height-tight);
  }
  
  .no-results__description {
    font-size: var(--font-size-base);
    color: var(--text-secondary);
    line-height: var(--line-height-relaxed);
    margin-bottom: 1.5rem;
    max-width: 400px;
  }
  
  .no-results__clear {
    padding: 0.5rem 1.5rem;
    background: var(--text-primary);
    border: none;
    border-radius: 6px;
    color: var(--bg-primary);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-semibold);
    cursor: pointer;
    transition: all 0.15s ease;
  }
  
  .no-results__clear:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  
  /* Customer Stories */
  .stories {
    padding: 4rem 0;
  }
  
  .stories-placeholder {
    text-align: center;
    padding: 4rem 2rem;
    background: var(--bg-primary);
    border: 1px solid var(--border-light);
    border-radius: 12px;
    max-width: 600px;
    margin: 0 auto;
  }
  
  .stories-placeholder svg {
    color: var(--text-tertiary);
    opacity: 0.5;
    margin-bottom: 1.5rem;
  }
  
  .stories-placeholder__title {
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: 1rem;
    line-height: var(--line-height-tight);
  }
  
  .stories-placeholder__description {
    font-size: var(--font-size-base);
    color: var(--text-secondary);
    line-height: var(--line-height-relaxed);
    max-width: 450px;
    margin: 0 auto;
  }
  
  /* Content Sections */
  .content-sections {
    padding: 4rem 0;
    background: var(--bg-secondary);
  }
  
  .content-sections__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
    gap: 2rem;
  }
  
  .content-section {
    background: var(--bg-primary);
    border: 1px solid var(--border-light);
    border-radius: 12px;
    padding: 1.5rem;
  }
  
  .content-section__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--border-light);
  }
  
  .content-section__title {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    line-height: var(--line-height-snug);
  }
  
  .content-section__link {
    color: var(--text-tertiary);
    text-decoration: none;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    transition: color 0.2s ease;
  }
  
  .content-section__link:hover {
    color: var(--text-primary);
  }
  
  .content-section__items {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .content-item {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 0.75rem;
    text-decoration: none;
    color: inherit;
    border-radius: 6px;
    transition: background-color 0.2s ease;
  }
  
  .content-item:hover {
    background: var(--bg-secondary);
  }
  
  .content-item--placeholder {
    cursor: default;
    opacity: 0.7;
  }
  
  .content-item--placeholder:hover {
    background: transparent;
  }
  
  .content-item__content {
    flex: 1;
  }
  
  .content-item__title {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-medium);
    color: var(--text-primary);
    margin-bottom: 0.25rem;
    line-height: var(--line-height-snug);
  }
  
  .content-item__meta {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: var(--font-size-xs);
    color: var(--text-tertiary);
  }
  
  .content-item__date {
    white-space: nowrap;
  }
  
  .content-item__category {
    padding: 0.125rem 0.375rem;
    background: var(--bg-tertiary);
    border-radius: 3px;
    font-weight: var(--font-weight-medium);
    text-transform: uppercase;
    letter-spacing: var(--letter-spacing-wider);
  }
  
  /* Responsive */
  @media (max-width: 1024px) {
    .featured-post {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
    
    .featured-post__image {
      height: 300px;
    }
    
    .content-sections__grid {
      grid-template-columns: 1fr;
    }
  }
  
  @media (max-width: 768px) {
    .container {
      padding: 0 1.5rem;
    }
    
    .hero__title {
      font-size: var(--font-size-3xl);
    }
    
    .hero__description {
      font-size: var(--font-size-base);
    }
    
    .hero__toolbar {
      flex-direction: column;
      align-items: stretch;
      gap: 1rem;
    }
    
    .hero__categories {
      justify-content: flex-start;
    }
    
    .hero__actions {
      width: 100%;
    }
    
    .featured-post__title {
      font-size: var(--font-size-3xl);
    }
    
    .recent__grid {
      grid-template-columns: 1fr;
    }
  }
  
  @media (max-width: 480px) {
    .hero {
      padding: 1.5rem 0 1rem;
    }
    
    .hero__title {
      font-size: var(--font-size-2xl);
    }
    
    .hero__description {
      font-size: var(--font-size-sm);
    }
    
    .category-btn {
      padding: 0.375rem 0.75rem;
      font-size: var(--font-size-xs);
    }
    
    .featured-post__title {
      font-size: var(--font-size-2xl);
    }
    
    .featured-post__description {
      font-size: var(--font-size-base);
    }
  }
</style>